import { useAuth0 } from '@brighte/passwordless-authenticator';
import { useTimer } from '../';
import { ApplicationFormStatus, useApplicationStatusQuery } from '../../graphql';
import { ApplicationSubmissionStatus } from '../../pages/ApplicationStatus/types';
import { useApplicationData } from '../../provider';
import { MAX_SYNC_STATUS_WAIT, getApplicantTypeAndTrackingIdFromUrl, instantApprovalProducts } from '../../utils';
import { getSubmissionStatus } from './helpers';

export const useGetApplicationSubmissionStatus = (): {
  status: ApplicationSubmissionStatus;
  time?: number;
  isStatusSettled: boolean;
} => {
  const { HouseholdDetails: householdDetails, quote } = useApplicationData(['HouseholdDetails', 'quote']);
  const { user, hasAccessToken } = useAuth0();

  const hasJointApplicant = householdDetails?.hasJointApplicant;
  const productIsInstantApproval = instantApprovalProducts.includes(quote?.financialProduct?.id ?? '');

  //get application status
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const { time, clearTimer } = useTimer({ max: MAX_SYNC_STATUS_WAIT, interval: 1000, direction: 'asc' });
  // Fetches the live status for every non-joint application, not just instant-approval, so terminal
  // states like Withdrawn are always reflected regardless of product type.
  // CT-3287: gate on the token itself, not just `user`. `user` is React state and lags a render
  // behind clearCredentials(), which wipes the token cache synchronously. createUseFetcher sets no
  // Authorization header when the cache is empty but still sends the request, which the gateway
  // rejects as UNAUTHENTICATED — surfacing a spurious "session expired" dialog.
  const isQueryEnabled = !!user && hasAccessToken() && !hasJointApplicant && time <= MAX_SYNC_STATUS_WAIT;

  // Only the instant-approval flow polls for a status transition (Submitted -> Approved/Declined).
  // Shared by refetchInterval and isStatusSettled below so the two can't drift apart.
  // CT-3287: the unresolved-status case is bounded by the timer too — previously only the
  // Submitted branch was. Without that bound an application whose status never resolves polls
  // every 5s indefinitely and never settles, so the session is never torn down. Nothing changes
  // for the customer: getSubmissionStatus already falls through to SUBMITTED at the cap, so the
  // only thing the old unbounded branch bought was background polling behind that same page.
  const willPoll = (status?: ApplicationFormStatus) =>
    productIsInstantApproval && time < MAX_SYNC_STATUS_WAIT && (!status || status === ApplicationFormStatus.Submitted);

  const {
    data: application,
    isFetched,
    isFetching,
  } = useApplicationStatusQuery(
    { applicationId: trackingId },
    {
      refetchInterval: data => (willPoll(data?.application?.status) ? 5000 : false),
      useErrorBoundary: true,
      enabled: isQueryEnabled,
    },
  );

  const applicationStatus = application?.application?.status;
  const submissionStatus = getSubmissionStatus(productIsInstantApproval, !!hasJointApplicant, time, applicationStatus);

  if (submissionStatus !== ApplicationSubmissionStatus.FETCHING) {
    clearTimer();
  }

  // CT-3287: the session may only be cleared once this hook will issue no further authenticated
  // requests — either the query never ran, or it has settled and polling has stopped. For
  // instant-approval products that means after the polling timer expires (or a terminal status
  // arrives), never on mount. `isFetching` stays true across react-query's retry backoff, so a
  // transient failure won't clear mid-retry.
  const isStatusSettled = !isQueryEnabled || (isFetched && !isFetching && !willPoll(applicationStatus));

  return {
    status: submissionStatus,
    time,
    isStatusSettled,
  };
};
